import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PRIMARY, BG_DARKER, BORDER, TEXT, TEXT_DIM, TEXT_FAINT } from '../theme/publicTokens';


// Strings traducibles del nav. Se mantienen acá (no en archivos i18n separados)
// porque por ahora solo PublicNav y PublicFooter son bilingues; agregar i18next
// completo es overkill para 2 componentes.
type Lang = 'es' | 'en';

const STRINGS: Record<Lang, {
  problema: string;
  metricas: string;
  comparativa: string;
  metodologia: string;
  vision: string;
  equipo: string;
  cta: string;
  ctaMobile: string;
  ariaHome: string;
  ariaBackHome: string;
  ariaOpenMenu: string;
  ariaCloseMenu: string;
  ariaNav: string;
}> = {
  es: {
    problema: 'Problema',
    metricas: 'Métricas',
    comparativa: 'Comparativa',
    metodologia: 'Metodología',
    vision: 'Visión',
    equipo: 'Equipo',
    cta: 'Sumarme →',
    ctaMobile: 'Sumarme a la lista de espera →',
    ariaHome: 'ECG.WATCH — ir al inicio',
    ariaBackHome: 'ECG.WATCH — volver a la home',
    ariaOpenMenu: 'Abrir menú',
    ariaCloseMenu: 'Cerrar menú',
    ariaNav: 'Menú de navegación',
  },
  en: {
    problema: 'Problem',
    metricas: 'Metrics',
    comparativa: 'Comparison',
    metodologia: 'Methodology',
    vision: 'Vision',
    equipo: 'Team',
    cta: 'Join →',
    ctaMobile: 'Join the waitlist →',
    ariaHome: 'ECG.WATCH — go to home',
    ariaBackHome: 'ECG.WATCH — back to home',
    ariaOpenMenu: 'Open menu',
    ariaCloseMenu: 'Close menu',
    ariaNav: 'Navigation menu',
  },
};


function AnchorLink({
  anchor,
  isHome,
  homeHref,
  children,
  className,
  style,
  onClick,
}: {
  anchor: string;
  isHome: boolean;
  homeHref: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}) {
  // Si estamos en la home (sea / o /en), el anchor es relativo al doc actual.
  // Si estamos en otra page, el anchor apunta a la home del idioma correspondiente.
  const href = isHome ? `#${anchor}` : `${homeHref}#${anchor}`;
  return (
    <a href={href} className={className} style={style} onClick={onClick}>
      {children}
    </a>
  );
}

export function PublicNav({ lang = 'es' }: { lang?: Lang } = {}) {
  const location = useLocation();
  // /en también es "home" — las secciones del landing en inglés viven en ese path.
  // Los IDs HTML siguen siendo en español (#problema, #metricas, ...) — son
  // identificadores internos, no se traducen.
  const isHome = location.pathname === '/' || location.pathname === '/en';
  const homeHref = lang === 'es' ? '/' : '/en';

  const t = STRINGS[lang];

  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const close = () => setMobileOpen(false);

  const wordmark = (
    <span className="flex items-center" style={{ textDecoration: 'none', gap: 10 }}>
      <img
        src="/apple-touch-icon.png"
        alt=""
        aria-hidden="true"
        style={{ width: 32, height: 32, borderRadius: 7, flexShrink: 0 }}
      />
      <span
        style={{
          fontFamily: "'Geist', sans-serif",
          fontWeight: 400,
          fontSize: 14,
          letterSpacing: '0.10em',
          color: TEXT,
          textTransform: 'uppercase',
        }}
      >
        ECG<span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#22D3EE', boxShadow: '0 0 8px rgba(34,211,238,0.9)', margin: '0 3px', verticalAlign: 'middle' }} aria-hidden="true" />WATCH
      </span>
    </span>
  );

  const desktopLinkStyle: React.CSSProperties = {
    color: 'inherit',
    textDecoration: 'none',
  };

  const mobileLinkStyle: React.CSSProperties = {
    color: TEXT,
    textDecoration: 'none',
    fontSize: 22,
    fontFamily: 'Fraunces, serif',
    fontWeight: 400,
    letterSpacing: '-0.01em',
    padding: '12px 0',
    borderBottom: `1px solid ${BORDER}`,
    display: 'block',
  };

  // Toggle ES/EN. Mismo path-pattern en ambos idiomas: link directo a / o /en.
  // El idioma activo se ve "encendido" (TEXT), el otro tenue (TEXT_DIM).
  const langToggle = (
    <div
      className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider"
      aria-label="Language switch"
    >
      <Link
        to="/"
        style={{
          color: lang === 'es' ? TEXT : TEXT_DIM,
          textDecoration: 'none',
          fontWeight: lang === 'es' ? 600 : 400,
        }}
        aria-current={lang === 'es' ? 'page' : undefined}
      >
        ES
      </Link>
      <span style={{ color: TEXT_FAINT }}>/</span>
      <Link
        to="/en"
        style={{
          color: lang === 'en' ? TEXT : TEXT_DIM,
          textDecoration: 'none',
          fontWeight: lang === 'en' ? 600 : 400,
        }}
        aria-current={lang === 'en' ? 'page' : undefined}
      >
        EN
      </Link>
    </div>
  );

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur"
        style={{
          background: `${BG_DARKER}E6`,
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          {isHome ? (
            <a
              href="#top"
              className="flex items-center gap-3"
              style={{ textDecoration: 'none' }}
              aria-label={t.ariaHome}
            >
              {wordmark}
            </a>
          ) : (
            <Link
              to={homeHref}
              className="flex items-center gap-3"
              style={{ textDecoration: 'none' }}
              aria-label={t.ariaBackHome}
            >
              {wordmark}
            </Link>
          )}

          <div
            className="hidden md:flex items-center gap-8 font-mono text-xs uppercase tracking-wider"
            style={{ color: TEXT_DIM }}
          >
            <AnchorLink anchor="problema" isHome={isHome} homeHref={homeHref} style={desktopLinkStyle}>
              {t.problema}
            </AnchorLink>
            <AnchorLink anchor="metricas" isHome={isHome} homeHref={homeHref} style={desktopLinkStyle}>
              {t.metricas}
            </AnchorLink>
            <Link to="/comparativa" style={desktopLinkStyle}>
              {t.comparativa}
            </Link>
            <Link to="/methodology" style={desktopLinkStyle}>
              {t.metodologia}
            </Link>
            <AnchorLink anchor="vision" isHome={isHome} homeHref={homeHref} style={desktopLinkStyle}>
              {t.vision}
            </AnchorLink>
            <Link to="/equipo" style={desktopLinkStyle}>
              {t.equipo}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-6">
            {langToggle}
            <AnchorLink
              anchor="lista-de-espera"
              isHome={isHome}
              homeHref={homeHref}
              className="font-mono text-xs uppercase tracking-wider px-4 py-2 rounded hover-lift"
              style={{
                color: PRIMARY,
                border: `1px solid ${PRIMARY}66`,
                textDecoration: 'none',
              }}
            >
              {t.cta}
            </AnchorLink>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden flex flex-col items-center justify-center"
            style={{
              background: 'transparent',
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              padding: 10,
              cursor: 'pointer',
              gap: 4,
            }}
            aria-label={t.ariaOpenMenu}
            aria-expanded={mobileOpen}
          >
            <span style={{ width: 18, height: 1.5, background: TEXT, display: 'block' }} />
            <span style={{ width: 18, height: 1.5, background: TEXT, display: 'block' }} />
            <span style={{ width: 18, height: 1.5, background: TEXT, display: 'block' }} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-[60]"
          style={{
            background: BG_DARKER,
            animation: 'publicNavDrawerIn 280ms ease-out',
          }}
          role="dialog"
          aria-modal="true"
          aria-label={t.ariaNav}
        >
          <div
            className="flex items-center justify-between"
            style={{
              padding: '16px 24px',
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <span className="flex items-center" style={{ gap: 10 }}>
              <img
                src="/apple-touch-icon.png"
                alt=""
                aria-hidden="true"
                style={{ width: 32, height: 32, borderRadius: 7, flexShrink: 0 }}
              />
              <span
                style={{
                  fontFamily: "'Geist', sans-serif",
                  fontWeight: 400,
                  fontSize: 14,
                  letterSpacing: '0.10em',
                  color: TEXT,
                  textTransform: 'uppercase',
                }}
              >
                ECG<span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#22D3EE', boxShadow: '0 0 8px rgba(34,211,238,0.9)', margin: '0 3px', verticalAlign: 'middle' }} aria-hidden="true" />WATCH
              </span>
            </span>
            <button
              type="button"
              onClick={close}
              style={{
                background: 'transparent',
                border: `1px solid ${BORDER}`,
                borderRadius: 6,
                padding: 10,
                cursor: 'pointer',
                color: TEXT,
                fontSize: 18,
                fontFamily: 'JetBrains Mono, monospace',
                lineHeight: 1,
                width: 40,
                height: 40,
              }}
              aria-label={t.ariaCloseMenu}
            >
              ✕
            </button>
          </div>

          <nav
            style={{
              padding: '24px 24px 0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <AnchorLink anchor="problema" isHome={isHome} homeHref={homeHref} style={mobileLinkStyle} onClick={close}>
              {t.problema}
            </AnchorLink>
            <AnchorLink anchor="metricas" isHome={isHome} homeHref={homeHref} style={mobileLinkStyle} onClick={close}>
              {t.metricas}
            </AnchorLink>
            <Link to="/comparativa" style={mobileLinkStyle} onClick={close}>
              {t.comparativa}
            </Link>
            <Link to="/methodology" style={mobileLinkStyle} onClick={close}>
              {t.metodologia}
            </Link>
            <AnchorLink anchor="vision" isHome={isHome} homeHref={homeHref} style={mobileLinkStyle} onClick={close}>
              {t.vision}
            </AnchorLink>
            <Link to="/equipo" style={mobileLinkStyle} onClick={close}>
              {t.equipo}
            </Link>
          </nav>

          <div style={{ padding: '24px 24px 0', display: 'flex', justifyContent: 'center' }}>
            {langToggle}
          </div>

          <div style={{ padding: '32px 24px' }}>
            <AnchorLink
              anchor="lista-de-espera"
              isHome={isHome}
              homeHref={homeHref}
              onClick={close}
              style={{
                display: 'block',
                textAlign: 'center',
                background: PRIMARY,
                color: BG_DARKER,
                padding: '16px 24px',
                borderRadius: 6,
                fontFamily: 'Instrument Sans, sans-serif',
                fontWeight: 500,
                fontSize: 15,
                textDecoration: 'none',
              }}
            >
              {t.ctaMobile}
            </AnchorLink>
          </div>

          <style>{`
            @keyframes publicNavDrawerIn {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
